import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import supabase, { clearSupabaseStorage } from '../utils/supabaseClient';
import { rolActual } from '../utils/adminUtils';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  // Consulta de rol en vuelo, para que dos componentes que preguntan a la vez
  // esperen la misma respuesta en lugar de competir.
  const promesaDelRol = useRef(null);

  useEffect(() => {
    // Check for active session on load
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      setUser(data.session?.user || null);
      setLoading(false);
    };
    
    checkSession();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user || null);
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error signing in:', error);
      throw error;
    }
  };

  // Sign in with Discord
  const signInWithDiscord = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'discord',
        options: {
          redirectTo: window.location.origin + '/gallery'
        }
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error signing in with Discord:', error);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      // Primero limpiamos el estado local
      setUser(null);
      setUserRole(null);
      
      // Limpiar la sesión de Supabase
      const { error } = await supabase.auth.signOut();
      
      // Limpiar todo el localStorage relacionado con la autenticación
      clearSupabaseStorage();
      
      // Limpiar también sessionStorage
      sessionStorage.clear();
      
      if (error) {
        console.error('Error signing out:', error);
        throw error;
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      
      // Aún si hay error, intentamos limpiar el almacenamiento
      clearSupabaseStorage();
      sessionStorage.clear();
      
      throw error;
    }
  };

  /**
   * Devuelve el rol del usuario.
   *
   * Antes esto eran hasta tres consultas: SELECT por email, SELECT por id y un
   * INSERT si no aparecia. Como `ProtectedRoute` lo llama en cada ruta
   * protegida, se pagaban en cada navegacion. Ahora el rol viaja dentro del
   * JWT como claim `user_role`, puesto por el Custom Access Token Hook, asi
   * que leerlo no sale a la red.
   *
   * El INSERT de la fila faltante tampoco vive mas aca: lo hace un trigger
   * sobre `auth.users`, que cubre por igual el alta por email y la de Discord
   * sin depender de que el navegador se acuerde de hacerlo.
   *
   * Ojo con `forceRefresh`: como el rol esta dentro del token, releerlo no
   * alcanza —hay que renovar la sesion—. Eso es lo que hace `rolActual(true)`,
   * y es la unica forma de que un cambio de rol aplique sin esperar a que el
   * token venza.
   *
   * Un `null` significa que el usuario no tiene fila en `user_roles`, o que el
   * hook no esta activo en el dashboard. No se traduce a 'viewer': son cosas
   * distintas, y asumir un rol donde no hay ninguno es justo lo que no
   * queremos que haga el codigo.
   */
  const getUserRole = useCallback(
    async (forceRefresh = false) => {
      if (userRole && !forceRefresh) return userRole;
      if (!user) return null;

      // Si ya hay una consulta en vuelo se ESPERA, no se devuelve lo que haya.
      // Antes decia `if (roleLoading) return userRole`, que ante dos llamadas
      // simultaneas —ProtectedRoute y Admin preguntan casi al mismo tiempo— le
      // devolvia null a la segunda. Admin leia ese null, concluia que no era
      // admin y navegaba a "/", que rebota a /login y termina en /gallery.
      // Deduplicar esta bien; deduplicar devolviendo un valor que todavia no
      // existe es responder cualquier cosa.
      if (promesaDelRol.current && !forceRefresh) return promesaDelRol.current;

      const enVuelo = (async () => {
        try {
          setRoleLoading(true);
          const rol = await rolActual(forceRefresh);
          setUserRole(rol);
          return rol;
        } catch (error) {
          console.error('Error leyendo el rol del token:', error);
          return null;
        } finally {
          setRoleLoading(false);
          promesaDelRol.current = null;
        }
      })();

      promesaDelRol.current = enVuelo;
      return enVuelo;
    },
    [user, userRole],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Establecer el usuario actual desde la sesión
    const setCurrentUser = async () => {
      try {
        setLoading(true);
        
        const { data } = await supabase.auth.getSession();
        
        if (data && data.session) {
          setUser(data.session.user);
          // Sin `true`: forzar aca dispara refreshSession(), que a su vez
          // emite onAuthStateChange, que vuelve a entrar. El token recien
          // llegado ya trae el claim, no hay nada que renovar.
          await getUserRole();
        } else {
          setUser(null);
          setUserRole(null);
        }
      } catch (error) {
        console.error('Error setting current user:', error);
      } finally {
        setLoading(false);
      }
    };

    setCurrentUser();

    // Escuchar cambios en la autenticación
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session && session.user) {
          setUser(session.user);
          // El rol viaja en el token nuevo, asi que se descarta el cacheado y
          // se relee. Forzar seria pedir otra sesion para leer la que acaba de
          // llegar, y ademas realimentaria este mismo listener.
          setUserRole(null);
          await getUserRole();
        } else {
          setUser(null);
          setUserRole(null);
        }
        setLoading(false);
      }
    );

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  const value = {
    user,
    loading,
    userRole,
    signUp,
    signIn,
    signInWithDiscord,
    signOut,
    getUserRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
